import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Check, ChevronDown, ChevronUp, Film, Loader, Lock, PenLine, Play, RefreshCw, Settings, SkipForward } from 'lucide-react';
import { Icon } from './Icon';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';
import {
  PIPELINE_STAGES_FE,
  PIPELINE_STATUS_META,
  STAGE_COLORS,
  DASHBOARD_BUCKETS,
} from '../constants/pipeline';
import { getPartBucket } from '../utils/pipelineUtils';
import { useToast } from '../toastContext';


// ── Full-screen stage cell ──
// Declared at module scope on purpose. When this lived inside
// ProjectKanbanPanel it got a new function identity on every render, so
// React unmounted and remounted every cell each time the parent updated.
// Typing in the notes textarea re-rendered the parent on each keystroke,
// remounting the textarea and sending the caret to the end of the text, so
// the middle of an existing note could not be edited. Keeping the identity
// stable fixes that; everything it needs is passed in as props.
function StageCell({ partNumber, stageObj, proj, projectRoot, theme, saving, setSaving, isLocked, allUsers, notesEdit, setNotesEdit, isAdmin, updateStage, skipStage, axiosAuth, silentRefresh, reportStageError }) {
    const row = proj.pipeline[`${stageObj.id}_${partNumber}`] || (stageObj.level === 'interview' ? { status: 'pending', stage: stageObj.id, part_number: 0 } : null);
    if (!row) {
      return (
        <td style={{ padding: '6px', textAlign: 'center', verticalAlign: 'top' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80px', borderRadius: '8px', border: `1px dashed ${theme.border}`, opacity: 0.25 }}>
            <Icon icon={Lock} size={12} color={theme.textSub} />
          </div>
        </td>
      );
    }

    const saveKey   = `${projectRoot}_${partNumber}_${stageObj.id}`;
    const isSaving  = !!saving[saveKey];
    const isAutoStage   = stageObj.id <= 3;
    const isOverridden  = isAutoStage && row.manual_override;
    const isComplete9   = stageObj.id === 9;

    // Lock logic
    const stagesInLevel = PIPELINE_STAGES_FE.filter(s => s.level === stageObj.level);
    const stageIdx      = stagesInLevel.findIndex(s => s.id === stageObj.id);
    const prevStage     = stageIdx > 0 ? stagesInLevel[stageIdx - 1] : null;
    const prevRow       = prevStage ? proj.pipeline[`${prevStage.id}_${partNumber}`] : null;
    const prevDone      = !prevStage
      || (prevRow && (prevRow.status === 'done' || prevRow.status === 'skipped'))
      || (!prevRow && prevStage.optional);
    const isAlreadyDone = row.status === 'done' || row.status === 'skipped';
    const isCellLocked = !isAlreadyDone && (!prevDone || isLocked);

    const meta = PIPELINE_STATUS_META[row.status] || PIPELINE_STATUS_META.pending;
    const stageCol = STAGE_COLORS[stageObj.id]?.bg || '#94a3b8';

    const cellBg = isCellLocked ? 'transparent'
      : row.status === 'done'    ? (isComplete9 ? 'rgba(34,197,94,0.12)' : `${stageCol}15`)
      : row.status === 'active'  ? 'rgba(245,158,11,0.10)'
      : row.status === 'skipped' ? 'rgba(167,139,250,0.10)'
      : 'transparent';

    const cellBorder = isCellLocked ? `1px dashed ${theme.border}`
      : row.status === 'done'    ? (isComplete9 ? '1px solid rgba(34,197,94,0.4)' : `1px solid ${stageCol}40`)
      : row.status === 'active'  ? '1px solid rgba(245,158,11,0.4)'
      : row.status === 'skipped' ? '1px solid rgba(167,139,250,0.35)'
      : `1px solid ${theme.border}`;

    const assignee = allUsers.find(u => u.id === row.assigned_user_id);
    const noteKey = `${stageObj.id}_${partNumber}`;
    const isEditingNotes = notesEdit?.key === noteKey;

    return (
      <td style={{ padding: '5px', verticalAlign: 'top' }}>
        <div style={{
          borderRadius: '10px', background: cellBg, border: cellBorder,
          padding: '10px', minHeight: '90px',
          opacity: isCellLocked ? 0.3 : 1,
          display: 'flex', flexDirection: 'column', gap: '6px',
        }}>
          {/* Status indicator + buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            {isSaving
              ? <Icon icon={Loader} size={12} color={meta.color} style={{ animation: 'spin 1s linear infinite' }} />
              : isCellLocked
              ? <Icon icon={Lock} size={11} color={theme.textSub} />
              : row.status === 'skipped'
              ? <Icon icon={SkipForward} size={12} color="#a78bfa" />
              : row.status === 'done'
              ? <Icon icon={Check} size={12} color={isComplete9 ? '#22c55e' : stageCol} />
              : row.status === 'active'
              ? <Icon icon={Play} size={11} color="#f59e0b" />
              : <div style={{ width: 9, height: 9, borderRadius: '50%', border: `2px solid ${theme.border}`, background: 'transparent' }} />
            }
            <span style={{ fontSize: '0.68rem', fontWeight: 700, color: isCellLocked ? theme.textSub : meta.color }}>
              {meta.label}
            </span>
            {isAutoStage && !isCellLocked && (
              <span style={{ fontSize: '0.52rem', color: isOverridden ? '#f59e0b' : '#3b82f6', fontWeight: 600 }}>
                {isOverridden ? 'manual' : 'auto'}
              </span>
            )}
          </div>

          {/* Status action buttons */}
          {!isCellLocked && (
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {['pending', 'active', 'done'].map(st => {
                const m = PIPELINE_STATUS_META[st];
                const active = row.status === st;
                return (
                  <button key={st} onClick={() => updateStage(partNumber, stageObj.id, { status: st })}
                    disabled={isSaving}
                    style={{
                      padding: '3px 8px', borderRadius: '6px', fontSize: '0.6rem', fontWeight: active ? 700 : 500,
                      border: `1px solid ${active ? m.color : theme.border}`,
                      background: active ? m.bg : 'transparent',
                      color: active ? m.color : theme.textSub,
                      cursor: isSaving ? 'wait' : 'pointer',
                    }}>
                    {m.label}
                  </button>
                );
              })}
              {isAdmin && stageObj.optional && row.status !== 'skipped' && (
                <button onClick={() => skipStage(partNumber, stageObj.id)}
                  style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '0.6rem', border: '1px solid #a78bfa', background: 'transparent', color: '#a78bfa', cursor: 'pointer' }}>
                  Skip
                </button>
              )}
              {isAdmin && isOverridden && (
                <button onClick={() => {
                  const k = saveKey;
                  setSaving(s => ({ ...s, [k]: true }));
                  axiosAuth.put('/pipeline/stage/reset-auto', { project_root: projectRoot, part_number: partNumber, stage: stageObj.id })
                    .then(silentRefresh).catch(err => reportStageError('Reset to auto', err))
                    .finally(() => setSaving(s => { const n = { ...s }; delete n[k]; return n; }));
                }}
                  style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '0.6rem', border: '1px solid #3b82f6', background: 'transparent', color: '#3b82f6', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <Icon icon={RefreshCw} size={8} /> Reset
                </button>
              )}
            </div>
          )}

          {/* Assignee dropdown */}
          {isAdmin && !isCellLocked && (
            <div>
              <div style={{ fontSize: '0.52rem', color: theme.textSub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>Assignee</div>
              <Select
                theme={theme}
                size="sm"
                value={row.assigned_user_id || ''}
                onChange={e => updateStage(partNumber, stageObj.id, { assigned_user_id: e.target.value ? parseInt(e.target.value) : null })}
                style={{ padding: '3px 6px', borderRadius: '5px', fontSize: '0.65rem' }}
              >
                <option value="">— unassigned —</option>
                {[...allUsers].sort((a, b) => (a.display_name || a.email || '').localeCompare(b.display_name || b.email || '')).map(u => <option key={u.id} value={u.id}>{u.display_name || u.email}</option>)}
              </Select>
            </div>
          )}
          {/* Show assignee for non-admins */}
          {!isAdmin && assignee && !isCellLocked && (
            <div style={{ fontSize: '0.6rem', color: theme.textSub }}>
              {assignee.display_name || assignee.email?.split('@')[0]}
            </div>
          )}

          {/* Notes */}
          {!isCellLocked && (
            <div>
              {isEditingNotes ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Input
                    multiline
                    theme={theme}
                    size="sm"
                    autoFocus
                    value={notesEdit.value}
                    onChange={e => setNotesEdit(prev => ({ ...prev, value: e.target.value }))}
                    onBlur={() => {
                      if (notesEdit.value !== (row.notes || '')) {
                        updateStage(partNumber, stageObj.id, { notes: notesEdit.value });
                      }
                      setNotesEdit(null);
                    }}
                    onKeyDown={e => { if (e.key === 'Escape') setNotesEdit(null); }}
                    rows={2}
                    style={{ padding: '4px 6px', borderRadius: '5px', border: `1px solid ${theme.glossaryAccent}`, fontSize: '0.6rem' }}
                  />
                </div>
              ) : (
                <div
                  onClick={(e) => { e.stopPropagation(); setNotesEdit({ key: noteKey, value: row.notes || '' }); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setNotesEdit({ key: noteKey, value: row.notes || '' }); } }}
                  role="button"
                  tabIndex={0}
                  title="Click to edit notes"
                  style={{
                    fontSize: '0.58rem', color: row.notes ? theme.text : theme.textSub,
                    fontStyle: row.notes ? 'normal' : 'italic',
                    cursor: 'text', padding: '2px 4px', borderRadius: '4px', minHeight: '16px',
                    border: `1px solid transparent`,
                    transition: '0.12s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = theme.border; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; }}
                >
                  {row.notes || 'Add notes…'}
                </div>
              )}
            </div>
          )}
        </div>
      </td>
    );
}

export default function ProjectKanbanPanel({ axiosAuth, currentUser, theme, isDarkMode, projectRoot, onBack, onOpenEditor, onOpenQA }) {
  const isAdmin = currentUser?.role === 'admin';
  const { pushToast } = useToast();

  const [proj, setProj]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [allUsers, setAllUsers]   = useState([]);
  const [saving, setSaving]       = useState({});
  const [notesEdit, setNotesEdit] = useState(null); // { key, value }
  // Bulk ops fire one PUT per tape. Keyed by stage id so the buttons for the
  // stage being written are disabled (a second tap used to fire the whole batch
  // again), and partial failures are reported instead of silently repainting as
  // a partial success.
  const [bulkBusy, setBulkBusy]   = useState({});
  const [bulkError, setBulkError] = useState('');

  const loadProject = useCallback(() => {
    axiosAuth.get('/pipeline/projects').then(r => {
      const match = (r.data || []).find(p => p.project_root === projectRoot);
      setProj(match || null);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [axiosAuth, projectRoot]);

  const silentRefresh = useCallback(() => {
    axiosAuth.get('/pipeline/projects').then(r => {
      const match = (r.data || []).find(p => p.project_root === projectRoot);
      if (match) setProj(match);
    }).catch(() => {});
  }, [axiosAuth, projectRoot]);

  useEffect(() => { loadProject(); }, [loadProject]);
  useEffect(() => {
    if (isAdmin) axiosAuth.get('/admin/users').then(r => setAllUsers((r.data || []).filter(u => u.is_active !== false))).catch(() => {});
  }, [axiosAuth, isAdmin]);

  // Single-cell stage writes used to swallow failures with .catch(() => {}).
  // There is no optimistic update, so on failure the spinner just cleared and
  // the button stayed on its old status — the click looked like it did nothing
  // and a reviewer could believe a stage had been marked done when it had not.
  // One toast id so a repeated failure replaces rather than stacks.
  const reportStageError = (label, err) => {
    const detail = err?.response?.data?.detail || err?.message || 'request failed';
    pushToast('kanban-stage-error', 'error', `${label} — not saved: ${detail}`);
  };

  const updateStage = (partNumber, stage, patch) => {
    const key = `${projectRoot}_${partNumber}_${stage}`;
    setSaving(s => ({ ...s, [key]: true }));
    axiosAuth.put('/pipeline/stage', { project_root: projectRoot, part_number: partNumber, stage, ...patch })
      .then(silentRefresh).catch(err => reportStageError('Stage update', err))
      .finally(() => setSaving(s => { const n = { ...s }; delete n[key]; return n; }));
  };

  const skipStage = (partNumber, stage) =>
    axiosAuth.put('/pipeline/stage/skip', { project_root: projectRoot, part_number: partNumber, stage })
      .then(silentRefresh).catch(err => reportStageError('Skip stage', err));

  // Runs the per-tape calls for one bulk action, then refreshes. Any rejected
  // call is surfaced — silently refreshing after a half-failed batch made a
  // partial result look like the intended one.
  const runBulk = async (stageObj, makeCall, label) => {
    if (bulkBusy[stageObj.id]) return;
    const parts = (proj?.parts || []).filter(p => p > 0);
    const targetParts = stageObj.level === 'part' ? parts : [0];
    if (!targetParts.length) return;
    setBulkBusy(b => ({ ...b, [stageObj.id]: true }));
    setBulkError('');
    try {
      const results = await Promise.allSettled(targetParts.map(makeCall));
      const failed = results.filter(r => r.status === 'rejected');
      if (failed.length) {
        const detail = failed[0].reason?.response?.data?.detail || failed[0].reason?.message || 'request failed';
        setBulkError(`${label} — ${failed.length} of ${results.length} tape${results.length === 1 ? '' : 's'} not saved: ${detail}`);
      }
      silentRefresh();
    } finally {
      setBulkBusy(b => { const n = { ...b }; delete n[stageObj.id]; return n; });
    }
  };

  // Bulk update: set all tapes for a given stage to a status
  const bulkUpdateStage = (stageObj, status) =>
    runBulk(
      stageObj,
      pn => axiosAuth.put('/pipeline/stage', { project_root: projectRoot, part_number: pn, stage: stageObj.id, status }),
      `${stageObj.label}: set to ${status}`,
    );

  const bulkSkipStage = (stageObj) =>
    runBulk(
      stageObj,
      pn => axiosAuth.put('/pipeline/stage/skip', { project_root: projectRoot, part_number: pn, stage: stageObj.id }),
      `${stageObj.label}: skip`,
    );

  const partStages      = PIPELINE_STAGES_FE.filter(s => s.level === 'part');
  const interviewStages = PIPELINE_STAGES_FE.filter(s => s.level === 'interview');
  const displayName = projectRoot?.replace(/_/g, ' ').replace(/\bpt\d+\b/gi, '').trim() || '';

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: theme.bg }}>
        <div style={{ color: theme.textSub, fontSize: '0.85rem' }}>Loading project…</div>
      </div>
    );
  }
  if (!proj) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: theme.bg, gap: '12px' }}>
        <div style={{ color: theme.textSub, fontSize: '0.85rem' }}>Project not found.</div>
        <Button variant="primary" size="sm" theme={theme} onClick={onBack}>
          Back to Board
        </Button>
      </div>
    );
  }

  const tapes = (proj.parts || []).filter(p => p > 0);

  // Project is complete when stage 9 is done — lock for non-admins
  const stage9Row = proj.pipeline?.['9_0'];
  const isProjectComplete = stage9Row?.status === 'done';
  const isLocked = isProjectComplete && !isAdmin;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: theme.bg, overflow: 'hidden' }}>
      {/* Main content area */}
      <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
        <div style={{ maxWidth: '100%', margin: '0 auto' }}>

        {/* Header — inside the same max-width container so it aligns with the grid */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <Button variant="secondary" size="sm" icon={ArrowLeft} theme={theme} onClick={onBack}>
            Board
          </Button>
          <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
            <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '1.15rem', fontWeight: 400, letterSpacing: '0.02em', color: theme.text, whiteSpace: 'nowrap' }}>
              {displayName}
            </span>
            <div style={{ fontSize: '0.68rem', color: theme.textSub, marginTop: '2px' }}>
              {tapes.length} tape{tapes.length === 1 ? '' : 's'} — {proj.progress_pct}% complete
            </div>
          </div>
          {/* Progress donut + complete pill */}
          {(() => {
            const pct = proj.progress_pct || 0;
            const sz = 52;
            const sw = 6;
            const r = (sz - sw) / 2;
            const circ = 2 * Math.PI * r;
            const offset = circ * (1 - pct / 100);
            const ringColor = pct >= 100 ? '#22c55e' : pct >= 75 ? '#34d399' : pct >= 50 ? '#4ade80' : pct >= 25 ? '#86efac' : '#bbf7d0';
            return (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                <div style={{ position: 'relative', width: sz, height: sz }}>
                  <svg width={sz} height={sz} viewBox={`0 0 ${sz} ${sz}`} style={{ display: 'block' }}>
                    <circle cx={sz/2} cy={sz/2} r={r} fill="none"
                      stroke={isDarkMode ? 'rgba(148,163,184,0.18)' : 'rgba(148,163,184,0.25)'}
                      strokeWidth={sw} />
                    <circle cx={sz/2} cy={sz/2} r={r} fill="none"
                      stroke={ringColor} strokeWidth={sw} strokeLinecap="round"
                      strokeDasharray={circ} strokeDashoffset={offset}
                      transform={`rotate(-90 ${sz/2} ${sz/2})`}
                      style={{ transition: 'stroke-dashoffset 0.4s, stroke 0.4s' }} />
                  </svg>
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 800, color: pct >= 100 ? '#22c55e' : theme.text }}>
                    {pct}%
                  </div>
                </div>
                {isProjectComplete && (
                  <span style={{ padding: '1px 7px', borderRadius: '999px', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.4)', fontSize: '0.5rem', fontWeight: 700, color: '#22c55e', whiteSpace: 'nowrap' }}>
                    {isAdmin ? 'Complete' : 'Locked'}
                  </span>
                )}
              </div>
            );
          })()}
        </div>

        {/* Bulk-action failures — outside the tables so the tight stage headers keep their layout */}
        {bulkError && (
          <div role="alert" style={{
            marginBottom: '14px', padding: '8px 11px', borderRadius: '8px',
            background: `${theme.danger}14`, border: `1px solid ${theme.danger}44`,
            color: theme.danger, fontSize: '0.7rem', fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: '10px',
          }}>
            <span style={{ flex: 1 }}>{bulkError}</span>
            <Button variant="danger-ghost" size="sm" theme={theme} onClick={() => setBulkError('')} style={{ flexShrink: 0 }}>
              Dismiss
            </Button>
          </div>
        )}

        {/* Part-level stages: tapes × stages */}
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <colgroup>
            <col style={{ width: '90px' }} />
            {partStages.map(s => <col key={s.id} />)}
          </colgroup>
          <thead>
            <tr>
              <th style={{ padding: '8px 6px', textAlign: 'left', verticalAlign: 'top' }}>
                <span style={{ fontSize: '0.6rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-flex', alignItems: 'center', gap: '3px' }}><Icon icon={Film} size={8} color={theme.textSub} /> Tape</span>
              </th>
              {partStages.map(s => {
                const sc = STAGE_COLORS[s.id]?.bg || '#94a3b8';
                return (
                  <th key={s.id} style={{ padding: '8px 6px', textAlign: 'center', verticalAlign: 'top' }}>
                    {/* Circle + label — fixed height so optional/non-optional align */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', minHeight: '58px' }}>
                      <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: sc, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ fontSize: '0.65rem', color: '#fff', fontWeight: 800 }}>{s.id}</span>
                      </div>
                      <span style={{ fontSize: '0.65rem', fontWeight: 600, color: theme.text }}>{s.label}</span>
                      {s.optional && <span style={{ fontSize: '0.5rem', color: '#a78bfa', fontWeight: 600 }}>optional</span>}
                    </div>
                      {/* Bulk status buttons — pushed to bottom */}
                      {isAdmin && !isLocked && (
                        <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '8px' }}>
                          {['pending', 'active', 'done'].map(st => {
                            const m = PIPELINE_STATUS_META[st];
                            return (
                              <button key={st} onClick={() => bulkUpdateStage(s, st)} title={`Set all tapes to ${m.label}`}
                                disabled={!!bulkBusy[s.id]}
                                style={{ padding: '2px 5px', borderRadius: '4px', fontSize: '0.48rem', fontWeight: 600, border: `1px solid ${m.color}50`, background: `${m.color}12`, color: m.color, cursor: bulkBusy[s.id] ? 'wait' : 'pointer', opacity: bulkBusy[s.id] ? 0.5 : 1, lineHeight: 1.2 }}>
                                {st === 'done' ? 'All Done' : st === 'active' ? 'All WIP' : 'All Reset'}
                              </button>
                            );
                          })}
                          {s.optional && (
                            <button onClick={() => bulkSkipStage(s)} title="Skip all tapes"
                              disabled={!!bulkBusy[s.id]}
                              style={{ padding: '2px 5px', borderRadius: '4px', fontSize: '0.48rem', fontWeight: 600, border: '1px solid #a78bfa50', background: '#a78bfa12', color: '#a78bfa', cursor: bulkBusy[s.id] ? 'wait' : 'pointer', opacity: bulkBusy[s.id] ? 0.5 : 1, lineHeight: 1.2 }}>
                              All Skip
                            </button>
                          )}
                        </div>
                      )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {tapes.map((pt, idx) => {
              const b = getPartBucket(proj, pt);
              const bm = DASHBOARD_BUCKETS.find(x => x.key === b);
              const isEven = idx % 2 === 0;
              return (
                <tr key={pt} style={{ background: isEven ? 'transparent' : (isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)') }}>
                  <td style={{ padding: '6px 8px', verticalAlign: 'top' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <Icon icon={Film} size={13} color={theme.textSub} />
                      <span style={{ fontSize: '0.82rem', fontWeight: 700, color: theme.text }}>Tape {pt}</span>
                      {bm && (
                        <span role="img" aria-label={bm.label} style={{ width: '8px', height: '8px', borderRadius: '50%', background: bm.color, flexShrink: 0 }} title={bm.label} />
                      )}
                    </div>
                    {/* Per-tape Edit button */}
                    <button
                      onClick={() => onOpenEditor(pt)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '4px',
                        padding: '3px 10px', borderRadius: '6px', fontSize: '0.6rem', fontWeight: 600,
                        border: `1px solid ${theme.glossaryAccent}`, background: `${theme.glossaryAccent}12`,
                        color: theme.glossaryAccent, cursor: 'pointer',
                      }}
                    >
                      <Icon icon={PenLine} size={10} /> Edit
                    </button>
                  </td>
                  {partStages.map(s => (
                    <StageCell key={s.id} partNumber={pt} stageObj={s} proj={proj} projectRoot={projectRoot} theme={theme} saving={saving} setSaving={setSaving}
                      isLocked={isLocked} allUsers={allUsers} notesEdit={notesEdit} setNotesEdit={setNotesEdit}
                      isAdmin={isAdmin} updateStage={updateStage} skipStage={skipStage}
                      axiosAuth={axiosAuth} silentRefresh={silentRefresh} reportStageError={reportStageError} />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Interview-level stages */}
        <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: `1.5px dashed ${theme.border}` }}>
          <div style={{ fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px', textAlign: 'center' }}>
            Interview-Level Stages
          </div>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <colgroup>
              {interviewStages.map(s => <col key={s.id} />)}
            </colgroup>
            <thead>
              <tr>
                {interviewStages.map(s => {
                  const sc = STAGE_COLORS[s.id]?.bg || '#94a3b8';
                  const isClickable = s.id === 6 && onOpenQA;
                  return (
                    <th key={s.id} style={{ padding: '8px 6px', textAlign: 'center', verticalAlign: 'top' }}>
                      {/* Circle + label — fixed height so optional/non-optional align */}
                      <div
                        onClick={isClickable ? onOpenQA : undefined}
                        onKeyDown={isClickable ? (e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenQA(); } }) : undefined}
                        role={isClickable ? 'button' : undefined}
                        tabIndex={isClickable ? 0 : undefined}
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', minHeight: '58px', cursor: isClickable ? 'pointer' : 'default' }}
                        title={isClickable ? 'Open QA Review' : undefined}
                      >
                        <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: sc, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ fontSize: '0.65rem', color: '#fff', fontWeight: 800 }}>{s.id}</span>
                        </div>
                        <span style={{ fontSize: '0.65rem', fontWeight: 600, color: isClickable ? theme.glossaryAccent : theme.text, textDecoration: isClickable ? 'underline' : 'none' }}>{s.label}</span>
                        {s.optional && <span style={{ fontSize: '0.5rem', color: '#a78bfa', fontWeight: 600 }}>optional</span>}
                      </div>
                      {isAdmin && !isLocked && (
                        <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '4px' }}>
                          {['pending', 'active', 'done'].map(st => {
                            const m = PIPELINE_STATUS_META[st];
                            return (
                              <button key={st} onClick={() => bulkUpdateStage(s, st)} title={`Set to ${m.label}`}
                                disabled={!!bulkBusy[s.id]}
                                style={{ padding: '2px 5px', borderRadius: '4px', fontSize: '0.48rem', fontWeight: 600, border: `1px solid ${m.color}50`, background: `${m.color}12`, color: m.color, cursor: bulkBusy[s.id] ? 'wait' : 'pointer', opacity: bulkBusy[s.id] ? 0.5 : 1, lineHeight: 1.2 }}>
                                {m.label}
                              </button>
                            );
                          })}
                          {s.optional && (
                            <button onClick={() => bulkSkipStage(s)} title="Skip"
                              disabled={!!bulkBusy[s.id]}
                              style={{ padding: '2px 5px', borderRadius: '4px', fontSize: '0.48rem', fontWeight: 600, border: '1px solid #a78bfa50', background: '#a78bfa12', color: '#a78bfa', cursor: bulkBusy[s.id] ? 'wait' : 'pointer', opacity: bulkBusy[s.id] ? 0.5 : 1, lineHeight: 1.2 }}>
                              Skip
                            </button>
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              <tr>
                {interviewStages.map(s => (
                  <StageCell key={s.id} partNumber={0} stageObj={s} proj={proj} projectRoot={projectRoot} theme={theme} saving={saving} setSaving={setSaving}
                      isLocked={isLocked} allUsers={allUsers} notesEdit={notesEdit} setNotesEdit={setNotesEdit}
                      isAdmin={isAdmin} updateStage={updateStage} skipStage={skipStage}
                      axiosAuth={axiosAuth} silentRefresh={silentRefresh} reportStageError={reportStageError} />
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        </div>
      </div>
    </div>
  );
}
